/**
 * CG-8 — useCoachMode hook (coach home & alerts).
 *
 * The nav-level coach detection, mirroring useGuardianMode's cached
 * pattern: does the signed-in user train ≥1 team, and how many plans
 * are waiting for their review (the Team-tab badge count + the Today
 * gate's coach-only routing signal)?
 *
 * Gated on the `coach_home_v1` server flag (/api/config): flag off →
 * no fetches, always the athlete default — the whole CG-8 layer stays
 * dark. Flag on → ONE cached fetch chain per user per session
 * (/api/trainer/teams, then pending-plans per team — the same
 * endpoints useCoachPlanReviews uses live on /approvals). Staleness
 * trade-off is the same as the admin/guardian gates: approving a
 * review updates the badge on the next full page load.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useAthleteContext } from './useAthleteContext';
import { useAuthFetch } from './useAuthFetch';
import { useServerFlag } from './useServerFlag';

export interface CoachModeState {
  /** True until auth + the coach check resolve (flag off resolves instantly). */
  loading: boolean;
  /** True when the user trains at least one team. */
  isCoach: boolean;
  /** Plans awaiting the caller's review across their teams (0 when the
   *  coach_plan_review_v1 endpoint reports disabled). */
  pendingReviews: number;
  /**
   * UAT-B4 — coach-only account: declared coach at the onboarding fork
   * (`_account_role === 'coach'`, the same durable flag the Today gate
   * trusts, P7a) AND no completed athlete onboarding. Role-derived from
   * athlete context — independent of `coach_home_v1` / the team fetch — so
   * the athlete-surface suppression holds even when that flag is off. A
   * DUAL account (onboarded athlete who also trains a team) is NOT
   * coachOnly, so the athlete app renders byte-identical.
   *
   * ⚖️ B47 fix 7 — STICKY for the current uid while the context read goes
   * around again (see the derivation below): false until the first settle,
   * then the last settled answer for that account, and false again the moment
   * the uid changes.
   *
   * ⚖️ B47 fix 9 (round-44 critic F1, B160) — AND ONLY FROM A ROW THAT SAYS
   * WHOSE IT IS. Both the write and the read are gated on the provider's
   * attribution now, so the commit an in-place switch lands on — `uid` already
   * the new account, the context provider's `loading` still false and `raw`
   * still the previous account's — reads false instead of filing the last
   * reader's coach-ness under the new uid.
   */
  coachOnly: boolean;
  /**
   * ⚖️ B47 fix 9 (round-44 critic F3a, B162) — ASK THE TEAM CHECK AGAIN.
   *
   * The check's effect deps are frozen after a mount and a failed check is
   * never cached, so `/api/trainer/teams` failing once left `isCoach` false for
   * the rest of the session with nothing able to re-ask — the defect
   * `useAccountRole.refetch` exists to fix, one hook over, feeding Today's
   * onboarding gate (`today.tsx:1133`). Today's "Try again" calls this beside
   * the role `refetch`, so one press re-asks everything the outage broke.
   *
   * It does NOT return the check to `loading`: the previous answer stands until
   * a new one lands, so nothing that watches this re-holds on a press.
   */
  refetch: () => void;
}

interface CoachCheck {
  isCoach: boolean;
  pendingReviews: number;
}

const cache = new Map<string, CoachCheck>();
/**
 * ⚖️ B47 fix 10 (round-52 critic F2) — THE ENTRY CARRIES THE MOMENT IT WAS
 * ISSUED, SO AN ABANDONED CHECK STOPS ANSWERING FOR THE NEXT MOUNT.
 *
 * `useAccountRole` had the same defect and has a deadline to cure it on: the
 * entry is deleted in a `finally` that never runs on a request that never
 * settles, so `/api/trainer/teams` on a half-open socket left the dead promise
 * here and `inflight.get(uid) ??` handed it to every mount after it, for the
 * rest of the session, with only a press able to clear it.
 *
 * This check has no deadline of its own (adding one would change what `loading`
 * means to eight consumers, and that is not this lane's), so the bound is read
 * where the entry is consulted: a mount that finds an entry older than the
 * window treats it as abandoned, drops it and asks for itself. Same window as
 * the role fetch's deadline, and the same accepted price — a second socket
 * against a host that is dead but still accepting.
 */
const INFLIGHT_ABANDONED_MS = 10_000;
const inflight = new Map<string, { at: number; check: Promise<CoachCheck> }>();
/** ⚖️ B47 fix 9 — the press token, `useAccountRole`'s shape: a request writes
 *  the cache only if no press has superseded it since it was issued.
 *
 *  ⚖️ B47 fix 12 (round-58 critic F1, B162/B173) — AND IT DOES NOT ORDER TWO
 *  REQUESTS AT THE SAME GENERATION. The abandoned-window drop above lets a
 *  second real check go out while the first is still unanswered, both at the
 *  same generation, so this counter alone cannot say which of two later
 *  answers to keep — landing order used to decide, which let an older-issued,
 *  slower check overwrite a newer one's already-cached answer. `issueSeq` /
 *  `lastCached` below fix that; see `useAccountRole.ts` for the fuller note. */
const generation = new Map<string, number>();
/** ⚖️ B47 fix 12 — the role hook's shape: `issueSeq` stamps a per-uid counter
 *  once, synchronously, the moment a REAL check goes out (never on a reused
 *  in-flight promise), and `lastCached` records the stamp the cache currently
 *  holds. A check may write only when its own stamp is at least that value,
 *  so the later-issued check always wins the cache, whichever socket answers
 *  first. `refetch()` deletes this account's entry along with the cache and
 *  the in-flight one, so a press's own check starts a fresh count. */
const lastCached = new Map<string, number>();
const issueSeq = new Map<string, number>();
/** ⚖️ B47 fix 9 — every mounted instance, so `refetch()` reaches all of them.
 *  The cache is module-level but the STATE is per instance (BottomTab, the
 *  settings sections, the team route and Today's retry surface all mount this
 *  hook), and dropping the cache entry alone would leave every one of them on
 *  its stale answer, because nothing would re-run their effects. */
const listeners = new Set<() => void>();

/** Test-only — clear the module-level coach-check cache. */
export function __resetCoachModeCache(): void {
  cache.clear();
  inflight.clear();
  generation.clear();
  lastCached.clear();
  issueSeq.clear();
}

const ATHLETE_DEFAULT: CoachCheck = { isCoach: false, pendingReviews: 0 };

export function useCoachMode(): CoachModeState {
  const flagOn = useServerFlag('coach_home_v1');
  const { loading: authLoading, session, user } = useAuthContext();
  const { raw, loading: contextLoading, rawIsMine } = useAthleteContext();
  const fetchAuthed = useAuthFetch();
  const [check, setCheck] = useState<{ loading: boolean } & CoachCheck>({
    loading: true,
    ...ATHLETE_DEFAULT,
  });
  /** Bumped by `refetch()` on every mounted instance — the check effect's one
   *  re-run trigger that is not an identity change (B47 fix 9). */
  const [refetchTick, setRefetchTick] = useState(0);

  const uid = user?.id ?? null;
  const hasSession = !!session;

  // ⚖️ B47 fix 9 — subscribe to the module-level nudge (see `listeners`).
  useEffect(() => {
    const bump = () => setRefetchTick((n) => n + 1);
    listeners.add(bump);
    return () => {
      listeners.delete(bump);
    };
  }, []);

  const refetch = useCallback(() => {
    // This account's answer only; another account's entry is still good. The
    // in-flight entry goes too, so a press supersedes a request already out
    // rather than re-attaching to it (the F4 shape, fixed in both hooks).
    if (uid) {
      cache.delete(uid);
      inflight.delete(uid);
      generation.set(uid, (generation.get(uid) ?? 0) + 1);
      // ⚖️ B47 fix 12 — the press's own check starts a fresh issue-order count
      // rather than being held to a stamp a dropped answer left behind.
      lastCached.delete(uid);
    }
    for (const bump of listeners) bump();
  }, [uid]);

  /**
   * ⚖️ B179 — AN IN-PLACE SWITCH MUST NOT ANSWER FOR THE NEW ACCOUNT WITH
   * THE OLD ONE'S TEAM CHECK.
   *
   * `check` had no reset on a `uid` change: the module cache is already
   * per uid (so the next real answer is the new account's), but the STATE
   * held the previous account's `isCoach` / `pendingReviews` — with
   * `loading` possibly already false — until the check effect below
   * settled, either synchronously from a stale cache entry or after a
   * fresh fetch lands. A consumer reading `isCoach/pendingReviews` on the
   * switch commit (Today's role surfaces) could read account A's answer
   * filed under account B for that stretch.
   *
   * Declared BEFORE the check effect so its setup runs first on the same
   * commit (React runs effect setups in declaration order): this always
   * lands before that effect's synchronous cache-hit `settle`, so the
   * correct new-account answer (from cache or a fresh fetch) overwrites
   * the reset rather than racing behind it.
   *
   * "Unless already loading" — skips a no-op `setState` on mount (already
   * `{loading:true,...ATHLETE_DEFAULT}`) and does not re-arm on every
   * render, only on a `uid` change.
   *
   * F6 (2026-09-21) — useLayoutEffect, not useEffect: a passive effect
   * commits one frame late, so a child reading `isCoach`/`pendingReviews`
   * could still paint account A's answer under account B's uid before this
   * fires. useLayoutEffect runs before the browser paints the switch.
   */
  useLayoutEffect(() => {
    setCheck((s) => (s.loading ? s : { loading: true, ...ATHLETE_DEFAULT }));
  }, [uid]);

  useEffect(() => {
    const settle = (c: CoachCheck) =>
      setCheck((s) =>
        !s.loading && s.isCoach === c.isCoach && s.pendingReviews === c.pendingReviews
          ? s
          : { loading: false, ...c },
      );

    if (!flagOn) {
      settle(ATHLETE_DEFAULT);
      return;
    }
    if (authLoading) return;
    if (!hasSession || !uid) {
      settle(ATHLETE_DEFAULT);
      return;
    }

    let cancelled = false;

    const cached = cache.get(uid);
    if (cached) {
      settle(cached);
      return;
    }

    // ⚖️ B47 fix 10 (round-52 critic F2) — a request that is still going to
    // answer somebody may stand in for this mount; one the window has passed on
    // may not, so this mount drops it and asks for itself.
    //
    // ⚖️ B47 fix 11 (round-56 critic F1) — and the drop does NOT advance the
    // generation: a check slower than the window is a slow check, not a
    // superseded one, so its answer still writes the cache and the mount after
    // it reads the fact with no request. Only a press supersedes (`refetch`
    // keeps its own advance).
    const outstanding = inflight.get(uid);
    const reusable =
      outstanding && Date.now() - outstanding.at < INFLIGHT_ABANDONED_MS
        ? outstanding.check
        : undefined;
    if (outstanding && !reusable) inflight.delete(uid);
    // ⚖️ B47 fix 9 — the generation this request belongs to (see `generation`).
    const issuedAt = generation.get(uid) ?? 0;
    const fetchCheck =
      reusable ??
      (async (): Promise<CoachCheck> => {
        // ⚖️ B47 fix 12 — stamped synchronously, before the first await, so
        // it is this check's place in line the instant it really goes out.
        const mySeq = (issueSeq.get(uid) ?? 0) + 1;
        issueSeq.set(uid, mySeq);
        // ⚖️ B47 fix 12 — issue order, not landing order: an answer may write
        // only if no press has superseded it (generation, fix 9) AND no
        // later-issued check already wrote a fresher answer (mySeq).
        const canWrite = () =>
          (generation.get(uid) ?? 0) === issuedAt && mySeq >= (lastCached.get(uid) ?? 0);
        const writeCache = (c: CoachCheck) => {
          if (canWrite()) {
            cache.set(uid, c);
            lastCached.set(uid, mySeq);
          }
        };
        const teamsRes = await fetchAuthed('/api/trainer/teams');
        if (!teamsRes.ok) throw new Error(`trainer teams check failed (${teamsRes.status})`);
        const teamsJson = (await teamsRes.json()) as { teams?: Array<{ id?: string }> };
        const teamIds = (teamsJson.teams || []).map((t) => t.id).filter(Boolean) as string[];
        if (teamIds.length === 0) {
          writeCache(ATHLETE_DEFAULT);
          return ATHLETE_DEFAULT;
        }
        const counts = await Promise.all(
          teamIds.map(async (teamId) => {
            const res = await fetchAuthed(
              `/api/trainer/pending-plans?teamId=${encodeURIComponent(teamId)}`,
            );
            if (!res.ok) return 0;
            const json = (await res.json()) as { enabled?: boolean; plans?: unknown[] };
            return json.enabled === true && Array.isArray(json.plans) ? json.plans.length : 0;
          }),
        );
        const result: CoachCheck = {
          isCoach: true,
          pendingReviews: counts.reduce((a, b) => a + b, 0),
        };
        writeCache(result);
        return result;
      })();
    // Only a request this mount really issued is stamped: reusing an entry may
    // never refresh its age, or the window would never pass.
    if (!reusable) inflight.set(uid, { at: Date.now(), check: fetchCheck });

    fetchCheck
      .then((c) => {
        if (!cancelled) setCheck({ loading: false, ...c });
      })
      .catch(() => {
        // Fail quiet — nav simply renders the athlete default. Nothing is
        // cached, so a press (or the next full load) asks again.
        if (!cancelled) setCheck({ loading: false, ...ATHLETE_DEFAULT });
      })
      .finally(() => {
        // ⚖️ B47 fix 9 — its OWN entry: a press has already replaced it.
        if (inflight.get(uid)?.check === fetchCheck) inflight.delete(uid);
      });

    return () => {
      cancelled = true;
    };
  }, [flagOn, authLoading, hasSession, uid, fetchAuthed, refetchTick]);

  const loading = flagOn && (authLoading || check.loading);

  // UAT-B4 — role-derived coach-only gate. Uses the durable `_account_role`
  // flag + the absence of athlete onboarding; the team fetch above is NOT a
  // prerequisite, so a coach who declared the role but hasn't set up a team yet
  // still suppresses athlete surfaces. Held false until the FIRST settle →
  // callers hold the skeleton rather than flash athlete UI; after that the
  // settled answer is sticky across a reload of the same account (fix 7).
  //
  // "No athlete onboarding" keys on `_onboarding_completed_at` FIRST (the same
  // durable flag the Today gate + server refusal trust — today.tsx:652,
  // api/index.js isSeasonWriteRefusedForRole), with the boolean as a fallback.
  // This keeps a DUAL account (onboarded athlete who also declared coach)
  // rendering the athlete app byte-identical even if the boolean drifted from
  // the completion date (the partial-write case useAthleteContext anticipates).
  const athleteOnboarded =
    raw._onboarding_completed_at != null || raw._onboarding_completed === true;
  const contextSettled = !authLoading && !contextLoading;
  const liveCoachOnly = raw._account_role === 'coach' && !athleteOnboarded;
  /**
   * ⚖️ B47 fix 9 (round-44 critic F1, B160) — SETTLED IS NOT ENOUGH; THE ROW
   * HAS TO BE THIS ACCOUNT'S.
   *
   * `AuthContext.onAuthStateChange` only calls `setSession`, and the context
   * provider raises its `loading` from an EFFECT — so an in-place switch (a
   * cross-tab SIGNED_IN, `setSession` from a deep link) produces one committed
   * render in which `uid` is already the new account, `contextLoading` is still
   * false and `raw` is still the previous account's row. `contextSettled` alone
   * was true on that commit, so a coach-only account's answer was written back
   * under the NEW uid one line after the uid check dropped it, and every
   * consumer — `BottomTab`, `settings.tsx`, `team.tsx` — treated athlete B as
   * coach-only for the whole of her context read (up to the 10 s deadline).
   *
   * The provider publishes the attribution fix 6b already tracked (`rawIsMine`),
   * and it is false on exactly that commit.
   */
  const attributed = contextSettled && rawIsMine;

  /**
   * ⚖️ B47 fix 7 (round-33 UX 1, critic F1) — THE LEGACY ANSWER IS STICKY
   * ACROSS A RELOAD OF THE SAME ACCOUNT.
   *
   * The `!contextLoading` factor above used to make this answer "not a coach"
   * for the WHOLE of any context read, whoever is signed in. That is what
   * forced `AthleteSurfaceGuard` to re-hold on every reload for an account
   * whose `/api/user/role` never resolved (fix 5's `|| !resolved`), and that
   * hold is what emptied Today for 8.2 s on every press of "Try again"
   * (round-33 UX 1, measured at 375 and 1440).
   *
   * A context RELOAD cannot change who this account is. So the last answer
   * this hook settled on for THIS uid stands while the read goes around again,
   * and the degraded coach keeps his own surface instead of the guard holding
   * a blank page in front of everybody to protect him.
   *
   * ⚠️ KEYED BY UID, AND A UID CHANGE CLEARS IT. A settled answer belongs to
   * one account: on an in-place switch (a cross-tab `SIGNED_IN`, `setSession`
   * from a deep link) the ref is dropped and this hook is back to false until
   * the NEW account's row settles — which is what fix 6's role-hook reset and
   * the guard's per-uid latch both depend on. Nothing here weakens either.
   *
   * The ref is written during render, like the guard's own latch: the value
   * depends only on props and state, so a double render writes it twice with
   * the same result.
   */
  const lastSettled = useRef<{ uid: string | null; value: boolean } | null>(null);
  if (lastSettled.current && lastSettled.current.uid !== uid) lastSettled.current = null;
  // ⚖️ B47 fix 9 — written and read only on a frame that is settled AND
  // attributed. Three consequences, all deliberate:
  //   · the switch commit reads `false` (nothing is written, and the uid line
  //     above has already dropped the previous account's answer);
  //   · a degraded coach reloading his OWN context keeps his sticky answer,
  //     which is fix 7's property and the reason the guard no longer blanks
  //     everybody's page to protect him;
  //   · a same-account read that FAILS (the provider nulls the stamp with the
  //     row) falls back to the sticky answer rather than reading a live `{}` as
  //     "not a coach" — the conservative direction, and the one that keeps a
  //     coach off the athlete surface when his read dies.
  if (attributed) lastSettled.current = { uid, value: liveCoachOnly };
  const coachOnly = attributed ? liveCoachOnly : (lastSettled.current?.value ?? false);

  return {
    loading,
    isCoach: flagOn ? check.isCoach : false,
    pendingReviews: flagOn ? check.pendingReviews : 0,
    coachOnly,
    refetch,
  };
}
